import { headerSlotInsetOptions } from '@/components/ui/pageFrame';
import { Stack, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { Pressable } from 'react-native';
import { getTokenValue } from 'tamagui';

import PSIcon from '@/components/ui/PSIcon';
import PSText from '@/components/ui/PSText';
import { COMPONENT_CATALOG, PAGE_CATALOG } from '@/src/lib/componentCatalog';

/**
 * Dev component gallery layout — the nested Stack for the `/components/*`
 * group (the `index` and one route per component).
 *
 * Not a modal — this is a plain pushed page, so `animation: 'slide_from_right'`
 * is the same forward-only push every other screen uses, and `gestureEnabled`
 * is left alone (the root Stack's default swipe-back applies, same as any
 * other screen). Every screen in the group shares the same minimal chrome —
 * opaque `$background` header, the component's catalog title, a plain back icon — which used to be
 * copy-pasted into each screen's own `<Stack.Screen options={{...}}>`; it's
 * declared once here instead. Opaque rather than transparent because no
 * gallery route has a hero for a header to float over (unlike the event
 * detail), and bare-icon back keeps the gallery visually out of the way.
 *
 * NO `PSToastHost` here, unlike `/auth/*` and `/delete-account/*`. Those mount
 * their own because on iOS they're natively-presented SHEETS and the root
 * host draws underneath them. This group is not a sheet, so the root host
 * (see the `inModalFlow` gate in app/_layout.tsx — `components` is not in it)
 * stays live and already draws over it. Mounting a second host here would
 * mean every toast in the gallery — including the "Trigger toast" demo button
 * on the index route — fires twice.
 */
export default function ComponentsLayout() {
  const router = useRouter();

  return (
    <>
      {/* Home mounts a `light` StatusBar and stays mounted underneath. */}
      <StatusBar style="dark" />
      <Stack
        screenOptions={({ route }) => ({
          ...headerSlotInsetOptions,
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: {
            backgroundColor: getTokenValue('$color.background'),
          },
          contentStyle: {
            backgroundColor: getTokenValue('$color.background'),
          },
          headerTitle: () => (
            <PSText.H1 color="$textDark">
              {[...PAGE_CATALOG, ...COMPONENT_CATALOG].find(
                (entry) => entry.href === `/components/${route.name}`
              )?.title ?? ''}
            </PSText.H1>
          ),
          headerLeft: () => (
            <Pressable onPress={() => router.back()}>
              <PSIcon name="arrow-back" />
            </Pressable>
          ),
          animation: 'slide_from_right',
        })}
      />
    </>
  );
}
